/**
 * 금액 유효성 검사 결과
 */
export interface AmountValidation {
  isValid: boolean;
  error?: string;
}

export interface AmountFormatOptions {
  /** 음수 허용 여부 */
  allowNegative?: boolean;
  /** 소수점 허용 여부 */
  allowDecimal?: boolean;
  /** 소수점 자리수 제한 */
  decimalPlaces?: number;
  /** 최소값 */
  min?: number;
  /** 최대값 */
  max?: number;
}

/**
 * 금액 포맷팅
 * @param input - 입력된 문자열
 * @param options - 포맷팅 옵션
 * @returns 포맷팅된 금액
 */
export const formatAmount = (input: string, options: AmountFormatOptions = {}): string => {
  const { allowNegative = false, allowDecimal = false, decimalPlaces } = options;

  let processed = input;

  // 숫자, 소수점, 마이너스만 남기기
  if (allowDecimal) {
    processed = processed.replace(/[^\d.-]/g, '');
  } else {
    processed = processed.replace(/[^\d-]/g, '');
  }

  // 음수 허용하지 않으면 마이너스 제거
  if (!allowNegative) {
    processed = processed.replace(/-/g, '');
  } else {
    // 첫 글자 외 마이너스 제거
    const firstChar = processed[0];
    processed = processed.replace(/-/g, '');
    if (firstChar === '-') {
      processed = '-' + processed;
    }
  }

  // 소수점 처리
  if (allowDecimal) {
    // 소수점 중복 제거
    const parts = processed.split('.');
    if (parts.length > 2) {
      processed = parts[0] + '.' + parts.slice(1).join('');
    }

    // 소수점 자리수 제한
    if (decimalPlaces !== undefined && parts.length === 2) {
      parts[1] = parts[1].slice(0, decimalPlaces);
      processed = parts.join('.');
    }

    // 맨앞에 소수점이 오면 자동으로 0 추가
    if (processed.startsWith('.')) {
      processed = '0' + processed;
    }
    if (processed.startsWith('-.')) {
      processed = '-0' + processed.slice(1);
    }
  }

  // 맨앞에 0 제거 (0.xxx는 유지)
  if (!allowDecimal) {
    processed = processed.replace(/^(-?)0+(\d)/, '$1$2');
  } else {
    processed = processed.replace(/^(-?)0+(\d)/, '$1$2');
  }

  // 빈 문자열이나 '-'만 있으면 그대로 반환
  if (processed === '' || processed === '-') {
    return processed;
  }

  // 천단위 콤마 삽입
  const isNegative = processed.startsWith('-');
  const absoluteValue = processed.replace('-', '');
  const [integerPart, decimalPart] = absoluteValue.split('.');

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  let result = formattedInteger;

  if (decimalPart !== undefined) {
    result += '.' + decimalPart;
  }

  return isNegative ? '-' + result : result;
};

/**
 * 금액에서 숫자만 추출
 * @param input - 포맷팅된 금액
 * @returns 숫자만 포함된 문자열
 */
export const deformatAmount = (input: string): string => {
  return input.replace(/[^\d.-]/g, '');
};

/**
 * 금액 유효성 검사
 * @param value - 금액 문자열
 * @param options - 검증 옵션
 * @returns 유효성 검사 결과
 */
export const validateAmount = (
  value: string,
  options: AmountFormatOptions = {},
): AmountValidation => {
  const { min, max } = options;

  const cleaned = deformatAmount(value);

  if (cleaned.length === 0 || cleaned === '-') {
    return { isValid: false, error: '' };
  }

  const numValue = parseFloat(cleaned);

  if (isNaN(numValue)) {
    return { isValid: false, error: '올바른 금액을 입력해주세요' };
  }

  if (min !== undefined && numValue < min) {
    return {
      isValid: false,
      error: `${formatAmount(min.toString())}원 이상 입력해주세요`,
    };
  }

  if (max !== undefined && numValue > max) {
    return {
      isValid: false,
      error: `${formatAmount(max.toString())}원까지 입력할 수 있어요`,
    };
  }

  return { isValid: true };
};
